import React, { useState, useEffect } from 'react';
import EmptyState from '../../components/EmptyState';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Tooltip,
  Grid,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import Switch from '@mui/material/Switch';
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
  Schedule,
  Person,
  Phone,
  Email,
  Badge,
  Home,
  ExpandMore,
  ExpandLess,
  AccessTime,
  MenuBook
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import principalAPI from '../../services/principalAPI';
import { useAuth } from '../../context/AuthContext';

const Teachers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const { user, logout } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [statusDialog, setStatusDialog] = useState({ open: false, teacher: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, teacher: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const response = await principalAPI.getTeachers();
        if (isMounted) {
          setTeachers(response.data.teachers || []);
        }
      } catch (error) {
        console.error('Error fetching teachers:', error);
        if (isMounted) {
          setTeachers([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleExpand = (teacherId) => {
    setExpandedId(expandedId === teacherId ? null : teacherId);
  };

  useEffect(() => {
    if (!highlightId || loading || teachers.length === 0) return;
    setExpandedId(highlightId);
    const el = document.getElementById(`teacher-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('highlight');
      return next;
    }, { replace: true });
  }, [highlightId, loading, teachers, setSearchParams]);

  const handleEdit = (e, teacherId) => {
    e.stopPropagation();
    navigate(`/principal/teachers/edit/${teacherId}`);
  };

  const handleStatusToggle = (e, teacher) => {
    e.stopPropagation();
    setStatusDialog({ open: true, teacher });
  };

  const handleDeleteClick = (e, teacher) => {
    e.stopPropagation();
    setDeleteDialog({ open: true, teacher });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.teacher) return;
    setDeleteLoading(true);
    try {
      await principalAPI.deleteTeacher(deleteDialog.teacher.id);
      setTeachers(teachers.filter(t => t.id !== deleteDialog.teacher.id));
      if (expandedId === deleteDialog.teacher.id) setExpandedId(null);
      setDeleteDialog({ open: false, teacher: null });
    } catch (error) {
      console.error('Error deleting teacher:', error);
      alert(error.response?.data?.message || 'Failed to delete teacher');
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmStatusChange = async () => {
    try {
      const newStatus = !statusDialog.teacher.user.is_active;
      await principalAPI.updateTeacherStatus(statusDialog.teacher.id, newStatus);
      setTeachers(teachers.map(t =>
        t.id === statusDialog.teacher.id
          ? { ...t, user: { ...t.user, is_active: newStatus } }
          : t
      ));
      setStatusDialog({ open: false, teacher: null });
    } catch (error) {
      console.error('Error updating teacher status:', error);
      alert('Failed to update teacher status');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const DetailItem = ({ icon, label, value }) => {
    if (!value) return null;
    return (
      <Box display="flex" alignItems="center" gap={1} py={0.5}>
        {icon}
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
          {label}:
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Manage Teachers
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/principal/dashboard')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="outlined"
            startIcon={<Schedule />}
            onClick={() => navigate('/principal/teachers/work-hours')}
            color="secondary"
          >
            Work Hours
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/principal/teachers/new')}
          >
            Add Teacher
          </Button>
        </Box>
      </Box>

      {/* Teachers List */}
      {teachers.length === 0 ? (
        <EmptyState
          icon={Person}
          title="No teachers found"
          description='Click "Add Teacher" to create one.'
          actionLabel="Add Teacher"
          onAction={() => navigate('/principal/teachers/new')}
        />
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {teachers.map((teacher) => {
            const isExpanded = expandedId === teacher.id;
            const name = teacher.user?.name || 'N/A';
            return (
              <Paper
                key={teacher.id}
                id={`teacher-${teacher.id}`}
                elevation={isExpanded ? 4 : 1}
                sx={{
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: isExpanded ? '1px solid' : '1px solid transparent',
                  borderColor: isExpanded ? 'primary.main' : 'transparent',
                  '&:hover': { boxShadow: 3 },
                }}
                onClick={() => toggleExpand(teacher.id)}
              >
                {/* Teacher Summary Row */}
                <Box display="flex" alignItems="center" px={2.5} py={1.5} gap={2}>
                  {/* Expand Icon */}
                  <Box sx={{ color: 'text.secondary' }}>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </Box>

                  {/* Avatar */}
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold" color="white">
                      {name.charAt(0).toUpperCase()}
                    </Typography>
                  </Box>

                  {/* Name & Employee ID */}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={600} noWrap>
                      {name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {teacher.employee_id || 'No ID'}
                    </Typography>
                  </Box>

                  {/* Status Chip */}
                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
                    <Switch
                      checked={teacher.user?.is_active || false}
                      onChange={(e) => handleStatusToggle(e, teacher)}
                      onClick={(e) => e.stopPropagation()}
                      color="primary"
                      size="small"
                    />
                    <Chip
                      label={teacher.user?.is_active ? 'Active' : 'Inactive'}
                      color={teacher.user?.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  {/* Phone */}
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                    <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {teacher.user?.phone || '—'}
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Box display="flex" gap={0.5} sx={{ flexShrink: 0 }}>
                    <Tooltip title="Edit Teacher">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => handleEdit(e, teacher.id)}
                        sx={{
                          bgcolor: 'primary.50',
                          '&:hover': { bgcolor: 'primary.100' },
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Teacher">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => handleDeleteClick(e, teacher)}
                        sx={{
                          bgcolor: 'error.50',
                          '&:hover': { bgcolor: 'error.100' },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Expanded Details */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Divider />
                  <Box px={3} py={2.5} sx={{ bgcolor: 'grey.50' }}>
                    <Grid container spacing={3}>
                      {/* Personal Information */}
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
                          Personal Information
                        </Typography>
                        <DetailItem
                          icon={<Person sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Full Name"
                          value={teacher.user?.name}
                        />
                        <DetailItem
                          icon={<Email sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Email"
                          value={teacher.user?.email}
                        />
                        <DetailItem
                          icon={<Phone sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Phone"
                          value={teacher.user?.phone}
                        />
                        <DetailItem
                          icon={<Phone sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Alt. Phone"
                          value={teacher.alternative_phone}
                        />
                      </Grid>

                      {/* Work Information */}
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
                          Work Information
                        </Typography>
                        <DetailItem
                          icon={<Badge sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Employee ID"
                          value={teacher.employee_id}
                        />
                        <DetailItem
                          icon={<MenuBook sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Subjects"
                          value={Array.isArray(teacher.subjects) ? teacher.subjects.join(', ') : teacher.subjects}
                        />
                        <DetailItem
                          icon={<AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Start Time"
                          value={teacher.start_time}
                        />
                        <DetailItem
                          icon={<AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="End Time"
                          value={teacher.end_time}
                        />
                      </Grid>

                      {/* Additional Details */}
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
                          Additional Details
                        </Typography>
                        <DetailItem
                          icon={<Home sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Address"
                          value={teacher.address}
                        />
                        {!teacher.alternative_phone && !teacher.address && !teacher.subjects?.length && !teacher.start_time && (
                          <Box mt={1} p={1.5} sx={{ bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                            <Typography variant="body2" color="info.main">
                              No additional details available. Click Edit to add more information.
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Results Summary */}
      {teachers.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {teachers.length} teacher{teachers.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, teacher: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
          Delete Teacher
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteDialog.teacher?.user?.name}</strong>
            {deleteDialog.teacher?.employee_id && (
              <> (Employee ID: <strong>{deleteDialog.teacher.employee_id}</strong>)</>
            )}
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, teacher: null })} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Confirmation Dialog */}
      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, teacher: null })} fullWidth maxWidth="sm">
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {statusDialog.teacher?.user?.is_active ? (
              <>
                Are you sure you want to <strong>deactivate</strong> {statusDialog.teacher?.user?.name}?
                <br /><br />
                <strong>Warning:</strong> The teacher will not be able to login when inactive.
              </>
            ) : (
              <>
                Are you sure you want to <strong>activate</strong> {statusDialog.teacher?.user?.name}?
                <br /><br />
                The teacher will be able to login again.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ open: false, teacher: null })}>Cancel</Button>
          <Button onClick={confirmStatusChange} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Teachers;
